import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X, SlidersHorizontal } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; // Added for SEO
import api from "../../api/axios";

const NosProductsPage = () => {
  const { categorySlug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [sortOrder, setSortOrder] = useState('asc');

  const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

  // SEO Helpers
  const pageTitle = categorySlug 
    ? `${categorySlug.replace(/-/g, ' ').toUpperCase()} | Electro Titan Maroc`
    : "Tous nos Produits Électroménagers | Electro Titan";

  const pageDescription = categorySlug
    ? `Découvrez notre sélection de ${categorySlug.replace(/-/g, ' ')} chez Electro Titan à Salé. Qualité garantie et meilleurs prix au Maroc.`
    : "Explorez le catalogue complet d'Electro Titan : équipements électriques, gros électroménagers, et installation à Tabriquet, Salé.";

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/products`);
        if (res.data.success) {
          let data = res.data.data;
          if (categorySlug) {
             data = data.filter(p => p.category_slug === categorySlug || p.category?.slug === categorySlug);
          }
          setProducts(data);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categorySlug]);

  const brands = useMemo(() => {
    const b = products.map(p => p.brand).filter(Boolean);
    return ['All', ...new Set(b)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedBrand !== 'All') {
      result = result.filter(p => p.brand === selectedBrand);
    }
    result.sort((a, b) => {
      const priceA = parseFloat(a.price);
      const priceB = parseFloat(b.price);
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA;
    });
    return result;
  }, [products, selectedBrand, sortOrder]);

  const ProductCard = ({ product }) => {
    const price = parseFloat(product.price);
    const oldPrice = product.old_price ? parseFloat(product.old_price) : null;
    const discount = (oldPrice && oldPrice > price) 
      ? Math.round(((oldPrice - price) / oldPrice) * 100) 
      : 0;

    const imageSrc = product.main_image.startsWith('/storage') 
      ? `${BACKEND_BASE}${product.main_image}` 
      : `${BACKEND_BASE}/storage/${product.main_image}`;

    return (
      <motion.div layout>
        <Link 
          to={`/product/${product.slug}`} 
          className="group flex flex-col bg-white p-4 transition-all duration-300 hover:shadow-2xl border border-gray-50 rounded-2xl h-full"
        >
          <div className="relative mb-4 h-40 w-full overflow-hidden sm:h-56">
            {discount > 0 && (
              <div className="absolute top-0 left-0 z-10 bg-[#ffaa00] px-3 py-1 text-[10px] font-black text-white uppercase tracking-wider rounded-br-lg">
                -{discount}%
              </div>
            )}
            <img 
              src={imageSrc} 
              alt={`${product.brand} ${product.title}`} // SEO Optimized Alt
              className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="flex flex-col text-center mt-auto">
            <span className="text-[10px] font-bold text-[#8A8A89] uppercase tracking-widest mb-1">
              {product.brand || 'Electrotitan'}
            </span>
            <h3 className="font-sans text-xs font-bold text-gray-800 line-clamp-2 min-h-[32px] mb-2 uppercase tracking-tight group-hover:text-[#ffaa00] transition-colors">
              {product.title}
            </h3>
            <div className="flex flex-col items-center gap-1">
              <span className="text-lg font-black text-[#ffaa00]">
                {product.price} <span className="text-[10px]">DH</span>
              </span>
              {product.old_price && (
                <span className="text-xs text-[#8A8A89] line-through font-bold">
                  {product.old_price} DH
                </span>
              )}
            </div>
          </div>
        </Link>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      {/* --- SEO START --- */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta key="description" name="description" content={pageDescription} />
        <meta key="og:title" property="og:title" content={pageTitle} />
        <meta key="og:description" property="og:description" content={pageDescription} />
        <meta key="og:type" property="og:type" content="website" />
        <link rel="canonical" href={window.location.href} />
        
        {/* Schema.org ItemList for the Catalog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": pageTitle,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": filteredProducts.length,
              "itemListElement": filteredProducts.map((p, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "url": `https://www.titanelectro.ma/product/${p.slug}`
              }))
            }
          })}
        </script>
      </Helmet>
      {/* --- SEO END --- */}

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tighter">
              {categorySlug ? categorySlug.replace(/-/g, ' ') : 'Tous les produits'}
            </h1>
            <p className="text-sm font-bold text-[#8A8A89]">
              {loading ? 'Chargement...' : `${filteredProducts.length} articles trouvés`}
            </p>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${sortOrder === 'asc' ? 'border-[#ffaa00] text-[#ffaa00]' : 'border-gray-100 text-gray-400'}`}
            >
              Prix: {sortOrder === 'asc' ? 'Bas' : 'Haut'}
            </button>

            <select 
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border border-gray-100 text-gray-400 outline-none focus:border-[#ffaa00] bg-white cursor-pointer"
            >
              {brands.map(b => (
                <option key={b} value={b}>{b === 'All' ? 'Marque: Toutes' : b}</option>
              ))}
            </select>
          </div>

          <button 
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden flex items-center justify-center gap-2 bg-gray-50 p-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-gray-100"
          >
            <SlidersHorizontal size={16} /> Filtres & Tri
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="animate-spin text-[#ffaa00]" size={40} />
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                <motion.div 
                  layout
                  className="grid grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
                >
                  {filteredProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </motion.div>

                {filteredProducts.length === 0 && (
                  <motion.div 
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="text-center py-20 text-gray-300 font-bold uppercase tracking-widest text-xs"
                  >
                    Aucun résultat pour ces filtres.
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE FILTER DRAWER */}
      <AnimatePresence>
        {showMobileFilters && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div 
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              className="fixed inset-y-0 left-0 w-[80%] max-w-xs bg-white z-50 p-8 lg:hidden shadow-2xl flex flex-col"
            >
              <div className="flex justify-between items-center mb-10">
                <h2 className="font-black uppercase tracking-widest text-lg">Filtres</h2>
                <button onClick={() => setShowMobileFilters(false)} className="p-2 bg-gray-50 rounded-full"><X size={20} /></button>
              </div>

              <div className="space-y-8">
                <div>
                  <label className="text-[10px] font-black uppercase text-[#8A8A89] block mb-3">Trier par prix</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={() => setSortOrder('asc')}
                      className={`py-3 rounded-xl text-[10px] font-bold border transition-all ${sortOrder === 'asc' ? 'border-[#ffaa00] bg-[#ffaa00]/5 text-[#ffaa00]' : 'border-gray-100 text-gray-400'}`}
                    >
                      Le moins cher
                    </button>
                    <button 
                      onClick={() => setSortOrder('desc')}
                      className={`py-3 rounded-xl text-[10px] font-bold border transition-all ${sortOrder === 'desc' ? 'border-[#ffaa00] bg-[#ffaa00]/5 text-[#ffaa00]' : 'border-gray-100 text-gray-400'}`}
                    >
                      Le plus cher
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black uppercase text-[#8A8A89] block mb-3">Filtrer par marque</label>
                  <select 
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="w-full p-4 rounded-xl text-[10px] font-black border border-gray-100 uppercase outline-none focus:border-[#ffaa00]"
                  >
                    {brands.map(b => (
                      <option key={b} value={b}>{b === 'All' ? 'Toutes les marques' : b}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button 
                onClick={() => setShowMobileFilters(false)}
                className="w-full mt-auto bg-black text-white py-4 rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] shadow-xl active:scale-95 transition-transform"
              >
                Afficher {filteredProducts.length} articles
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NosProductsPage;